import React, { useState, useEffect, useCallback, useRef } from "react";
import { Camera, Trash, X, MagnifyingGlass, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";

interface TicketPhoto {
  id: number;
  sharePointFileId: string;
  sharePointFileUrl: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  imageFound: boolean;
  createdAt: string;
  label: string[];
}

interface TicketPhotosSectionProps {
  ticket: any;
}

export const TicketPhotosSection: React.FC<TicketPhotosSectionProps> = ({ ticket }) => {
  const { session } = useSession();
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [photos, setPhotos] = useState<TicketPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState<TicketPhoto | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadPhotos = useCallback(async () => {
    if (!ticket?.id || !session) return;
    setLoading(true);
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/file/helpdesk-ticket/${ticket.id}/photos`,
        { method: "GET" },
        session,
      );
      if (!resp.ok) throw new Error(`Fout bij ophalen foto's: ${resp.status}`);
      const data = await resp.json();
      setPhotos(data);
    } catch (err: any) {
      console.error("❌ TicketPhotosSection loadPhotos:", err);
      toast.error("Kon foto's niet laden.");
    } finally {
      setLoading(false);
    }
  }, [ticket?.id, session]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  useEffect(() => {
    if (!lightboxPhoto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxPhoto(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxPhoto]);

  useEffect(() => {
    if (lightboxPhoto) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [lightboxPhoto]);

  const uploadFiles = async (files: FileList | File[]) => {
    if (!ticket?.id || !session || !user?.id) return;

    const MAX_SIZE = 20 * 1024 * 1024;
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      if (file.size > MAX_SIZE) {
        toast.error(`Bestand "${file.name}" is te groot (max 20 MB).`);
        return;
      }
    }

    setUploading(true);
    try {
      for (const file of fileArray) {
        const formData = new FormData();
        formData.append("file", file);

        const resp = await fetchWithAuth(
          `${API_URL}/file/helpdesk-ticket/${ticket.id}/photos`,
          {
            method: "POST",
            body: formData,
            headers: { "x-user-id": user.id },
          },
          session,
        );

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err?.message || `Upload mislukt (${resp.status})`);
        }
      }
      toast.success(
        fileArray.length === 1
          ? "Foto succesvol toegevoegd."
          : `${fileArray.length} foto's succesvol toegevoegd.`,
      );
      await loadPhotos();
    } catch (err: any) {
      console.error("❌ TicketPhotosSection upload:", err);
      toast.error(err?.message || "Upload mislukt.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      uploadFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (photo: TicketPhoto) => {
    if (!session) return;
    setDeletingId(photo.id);
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/file/helpdesk-ticket/${ticket.id}/photos/${photo.id}`,
        { method: "DELETE" },
        session,
      );
      if (!resp.ok) throw new Error(`Verwijderen mislukt (${resp.status})`);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      if (lightboxPhoto?.id === photo.id) setLightboxPhoto(null);
      toast.success("Foto verwijderd.");
    } catch (err: any) {
      console.error("❌ TicketPhotosSection delete:", err);
      toast.error("Kon foto niet verwijderen.");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (raw: string) => {
    const d = new Date(raw);
    return isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  return (
    <div className="bg-muted border-muted-foreground/20 overflow-hidden rounded-lg border">
      {/* Header */}
      <div className="border-neutral-primary/10 flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Camera size={18} className="text-moss-primary" weight="duotone" />
          <h3 className="text-neutral-primary text-base font-semibold">
            Foto's
            {photos.length > 0 && (
              <span className="text-neutral-secondary-mid ml-2 text-sm font-normal">
                ({photos.length})
              </span>
            )}
          </h3>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="gap-1.5"
        >
          {uploading ? (
            <Spinner className="h-3 w-3" />
          ) : (
            <UploadSimple size={14} />
          )}
          Foto toevoegen
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {/* Body */}
      <div className="p-4">
        {/* Drop zone */}
        <div
          className={`mb-4 cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
            isDragging
              ? "border-moss-primary bg-moss-primary/10"
              : "border-neutral-primary/20 hover:border-neutral-primary/40"
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-secondary-mid">
              <Spinner className="h-4 w-4" />
              Foto's uploaden...
            </div>
          ) : (
            <p className="text-sm text-neutral-secondary-mid">
              Sleep foto's hier of{" "}
              <span className="text-moss-primary font-medium">klik om te uploaden</span>
            </p>
          )}
        </div>

        {/* Photo grid */}
        {loading ? (
          <div className="flex justify-center py-6">
            <Spinner className="h-5 w-5" />
          </div>
        ) : photos.length === 0 ? (
          <div className="py-6 text-center">
            <Camera size={32} className="text-neutral-primary/20 mx-auto mb-2" />
            <p className="text-sm text-neutral-secondary-mid">Nog geen foto's toegevoegd</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((photo) => (
              <PhotoThumbnail
                key={photo.id}
                photo={photo}
                isDeleting={deletingId === photo.id}
                onView={() => setLightboxPhoto(photo)}
                onDelete={() => handleDelete(photo)}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <div
            className="relative max-h-full max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxPhoto.imageUrl ? (
              <img
                src={lightboxPhoto.imageUrl}
                alt="Ticket foto"
                className="max-h-[85vh] max-w-[85vw] rounded object-contain"
              />
            ) : (
              <div className="flex h-64 w-64 items-center justify-center rounded bg-neutral-800">
                <Camera size={48} className="text-white/40" />
              </div>
            )}
            <div className="absolute right-2 top-2 flex gap-2">
              {lightboxPhoto.sharePointFileUrl && (
                <a
                  href={lightboxPhoto.sharePointFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                  title="Downloaden"
                >
                  <DownloadSimple size={20} />
                </a>
              )}
              <button
                onClick={() => setLightboxPhoto(null)}
                className="rounded-lg bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                title="Sluiten"
              >
                <X size={20} />
              </button>
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded bg-black/50 px-3 py-1 text-sm text-white">
              {formatDate(lightboxPhoto.createdAt)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface PhotoThumbnailProps {
  photo: TicketPhoto;
  isDeleting: boolean;
  onView: () => void;
  onDelete: () => void;
  formatDate: (d: string) => string;
}

const PhotoThumbnail: React.FC<PhotoThumbnailProps> = ({
  photo,
  isDeleting,
  onView,
  onDelete,
  formatDate,
}) => {
  return (
    <div className="group relative overflow-hidden rounded-lg border border-neutral-primary/10 bg-neutral-primary/5">
      {/* Thumbnail */}
      <div className="aspect-square w-full overflow-hidden">
        {photo.thumbnailUrl || photo.imageUrl ? (
          <img
            src={photo.thumbnailUrl || photo.imageUrl!}
            alt="Ticket foto"
            className="h-full w-full cursor-pointer object-cover transition-transform group-hover:scale-105"
            onClick={onView}
          />
        ) : (
          <div
            className="flex h-full w-full cursor-pointer items-center justify-center bg-neutral-primary/10"
            onClick={onView}
          >
            <Camera size={28} className="text-neutral-primary/30" />
          </div>
        )}
      </div>

      {/* Overlay actions (visible on hover) */}
      <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          onClick={onView}
          className="rounded-md bg-white/20 p-1.5 text-white transition-colors hover:bg-white/30"
          title="Vergroot"
        >
          <MagnifyingGlass size={16} />
        </button>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="rounded-md bg-red-500/70 p-1.5 text-white transition-colors hover:bg-red-500 disabled:opacity-50"
          title="Verwijderen"
        >
          {isDeleting ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <Trash size={16} />
          )}
        </button>
      </div>

      {/* Date */}
      <div className="border-neutral-primary/10 border-t px-2 py-1 text-center text-xs text-neutral-secondary-mid">
        {formatDate(photo.createdAt)}
      </div>
    </div>
  );
};
